import { FieldArray, Formik, useFormikContext } from "formik";
import { useEffect } from "react";
import apiRequest from "../../../utils/admin/apiRequest";
import InputField from "../../ui/form/InputField";
import Button from "../../ui/utils/Button";
import SelectField from "../../ui/form/SelectField";
import usePost from "../../../hooks/api/usePost";
import Alert from "../../ui/utils/Alert";
import useFetch from "../../../hooks/api/useFetch";
import { createVendor } from "../../../validation/vendor/authValidation";
import { CgAdd } from "react-icons/cg";
import { Trash } from "lucide-react";
import { generateUserId } from "../../../utils/helper";
import AsyncSearchSelect from "../../ui/form/AsyncSearchSelect";

const LocationWatcher = ({
  fetchStates,
  fetchCities,
  setBlankResponse,
  setCityBlankResponse,
}) => {
  const { values, setFieldValue } = useFormikContext();

  useEffect(() => {
    if (values.countryId) {
      fetchStates({ countryId: values.countryId });

      if (!values.stateId) {
        setFieldValue("stateId", "");
      }
    } else {
      setBlankResponse();
    }
  }, [values.countryId]);

  useEffect(() => {
    if (values.stateId) {
      fetchCities({ stateId: values.stateId });

      if (!values.cityId) {
        setFieldValue("cityId", "");
      }
    } else {
      setCityBlankResponse();
    }
  }, [values.stateId]);

  return null;
};



const CreateVendor = ({ closeModal, callback }) => {
  const { error, isLoading: loading, post, serverErrors } = usePost(
    apiRequest.createVendor
  );

  const { data: adminList } = useFetch({
    apiMethod: apiRequest.getHeadAdmin,
    body: { paginate: false, sortField: "name", sortOrder: "asc" },
    autoFetch: true,
  });


  const initialValues = {
    name: "",
    countryId: "6937b28dda25e4d40acc2983",
    stateId: "6937b35cda25e4d40acc4ce4",
    cityId: "",
    mobile: "",
    email: "",
    contactPerson: "",
    userId: "",
    password: "",   
    displayName: "superVendor",
    vendorType: "superVendor",

    isSuperVendor: true,
    addedBy: null,

    userDetails: [
      {
        name: "",
        mobile: "",
        email: "",
      },
    ],

    corporateCodeId: [""],

  };


  const UserIdWatcher = ({ generateUserId }) => {
    const { values, setFieldValue } = useFormikContext();

    useEffect(() => {
      const corpName = values.mobile;
      if (values.name && corpName) {
        setFieldValue("userId", generateUserId(values.name, corpName));
      }
    }, [values.name, values.mobile]);

    return null;
  };


  const { data: countryData } = useFetch({
    apiMethod: apiRequest.getCountry,
    body: { paginate: false, sortField: "name", sortOrder: "asc" },
  });

  const { data: stateData, refetch: fetchStates, setBlankResponse } = useFetch({
    apiMethod: apiRequest.getStateByCountryId,
    autoFetch: false,
  });

  const {
    data: cityData,
    refetch: fetchCities,
    setBlankResponse: setCityBlankResponse,
  } = useFetch({
    apiMethod: apiRequest.getCityByState,
    autoFetch: false,
  });

  const fetchCorporateCodes = async (search) => {
    const res = await apiRequest.getCorporateCodeForSelect({
      body: { filters: { name: { $regex: search, $options: "i" }, }, paginate: false, },
    });

    return (res?.data?.data?.corporatecodes?.map((v) => ({
      label: `${v.name} (${v.code})`,
      value: v.id,
    })) || []);
  };




  return (
    <div>
      <h2 className="p-4 text-lg font-semibold">Create Vendor Profile</h2>
      <div className="border-b" />

      <Formik
        initialValues={initialValues}
        validationSchema={createVendor}
        validateOnBlur
        validateOnChange
        onSubmit={async (values, { resetForm }) => {
          const response = await post({ data: { body: values } });
          if (response.response) {
            callback?.();
            resetForm();
            closeModal();
          }
        }}
      >
        {({ values, handleChange, handleBlur, handleSubmit, touched, errors }) => (
          <>
            <LocationWatcher
              fetchStates={fetchStates}
              fetchCities={fetchCities}
              setBlankResponse={setBlankResponse}
              setCityBlankResponse={setCityBlankResponse}
            />
            
            <UserIdWatcher generateUserId={generateUserId} />

          
            <form onSubmit={handleSubmit} className="text-sm max-h-[80vh] overflow-auto">
              {error && <Alert message={error} type="error" />}

              <div className="grid grid-cols-1 gap-2 p-4 sm:grid-cols-3">
                <InputField
                  name="name"
                  label="Vendor's Company Name"
                  value={values.name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.name && errors.name}
                  serverError={serverErrors?.name}
                />

                <InputField
                  name="mobile"
                  label="Mobile"
                  value={values.mobile}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.mobile && errors.mobile}
                  serverError={serverErrors?.mobile}
                />

                <InputField
                  name="email"
                  label="Email"
                  value={values.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.email && errors.email}
                  serverError={serverErrors?.email}
                />

                <SelectField
                  name="countryId"
                  label="Country"
                  value={values.countryId}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  options={countryData?.countries?.map((c) => ({
                    value: c._id,
                    label: c.name,
                  }))}
                  error={touched.countryId && errors.countryId}
                  serverError={serverErrors?.countryId}
                />

                <SelectField
                  name="stateId"
                  label="State"
                  value={values.stateId}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  options={stateData?.states?.map((s) => ({
                    value: s._id,
                    label: s.name,
                  }))}
                  error={touched.stateId && errors.stateId}
                  serverError={serverErrors?.stateId}
                />

                <SelectField
                  name="cityId"
                  label="City"
                  value={values.cityId}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  options={cityData?.cities?.map((c) => ({
                    value: c._id,
                    label: c.name,
                  }))}
                  error={touched.cityId && errors.cityId}
                  serverError={serverErrors?.cityId}
                />

                <InputField
                  name="contactPerson"
                  label="Contact Person"
                  value={values.contactPerson}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.contactPerson && errors.contactPerson}
                  serverError={serverErrors?.contactPerson}
                />

                <InputField
                  name="userId"
                  label="User ID"
                  value={values.userId}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.userId && errors.userId}
                  serverError={serverErrors?.userId}

                />

                <InputField
                  type="password"
                  name="password"
                  label="Password"
                  value={values.password}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.password && errors.password}
                  serverError={serverErrors?.password}

                />

                <div className="col-span-full">
                  <h3 className="font-semibold mb-2">Partner Details</h3>

                  <FieldArray name="userDetails">
                    {({ push, remove }) => (
                      <>
                        <table className="w-full border">
                          <thead>
                            <tr>
                              <th className="p-2 border">Name</th>
                              <th className="p-2 border">Mobile</th>
                              <th className="p-2 border">Email</th>
                              <th className="p-2 border text-center">Action</th>
                            </tr>
                          </thead>

                          <tbody>
                            {values.userDetails.map((row, index) => (
                              <tr key={index}>

                                {/* NAME */}
                                <td className="p-2 border">
                                  <InputField
                                    name={`userDetails.${index}.name`}
                                    value={row.name}
                                    placeholder="Enter name"
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={
                                      touched.userDetails?.[index]?.name &&
                                      errors.userDetails?.[index]?.name
                                    }
                                    serverError={serverErrors?.[`userDetails.${index}.name`]}
                                  />
                                </td>

                                {/* MOBILE */}
                                <td className="p-2 border">
                                  <InputField
                                    name={`userDetails.${index}.mobile`}
                                    value={row.mobile}
                                    onChange={handleChange}
                                    placeholder="Enter mobile"

                                    onBlur={handleBlur}
                                    error={
                                      touched.userDetails?.[index]?.mobile &&
                                      errors.userDetails?.[index]?.mobile
                                    }
                                    serverError={serverErrors?.[`userDetails.${index}.mobile`]}
                                  />
                                </td>

                                {/* EMAIL */}
                                <td className="p-2 border">
                                  <InputField
                                    name={`userDetails.${index}.email`}
                                    value={row.email}
                                    onChange={handleChange}
                                    placeholder="Enter email"

                                    onBlur={handleBlur}
                                    error={
                                      touched.userDetails?.[index]?.email &&
                                      errors.userDetails?.[index]?.email
                                    }
                                    serverError={serverErrors?.[`userDetails.${index}.email`]}
                                  />
                                </td>

                                {/* REMOVE BUTTON */}
                                <td className="text-center p-2 border">
                                  <button
                                    type="button"
                                    onClick={() => remove(index)}
                                    className="text-red-500"
                                  >
                                    <Trash size={17} />
                                  </button>
                                </td>

                              </tr>
                            ))}
                          </tbody>
                        </table>

                        <div className="flex justify-end">
                          <button
                            type="button"
                            onClick={() =>
                              push({ name: "", mobile: "", email: "" })
                            }
                            className="mt-2 px-3 py-1 bg-blue-500 text-white text-xs rounded flex items-center gap-2"
                          >
                            <CgAdd /> Add User
                          </button>
                        </div>
                      </>
                    )}
                  </FieldArray>

                </div>

                <div className="col-span-full">
                  <FieldArray name="corporateCodeId">
                    {({ push, remove }) => (
                      <>
                        {values.corporateCodeId.map((_, index) => (
                          <div
                            key={index}
                            className="flex items-center gap-2 mb-2"
                          >
                            <AsyncSearchSelect
                              name={`corporateCodeId.${index}`}
                              placeholder="Select Corporate Code"
                              value={values.corporateCodeId[index]}
                              onChange={handleChange}
                              fetchOptions={fetchCorporateCodes}
                              error={touched.corporateCodeId && errors.corporateCodeId}
                            />

                            <button
                              type="button"
                              onClick={() => remove(index)}
                              className="text-red-500"
                            >
                              <Trash size={16} />
                            </button>
                          </div>
                        ))}

                        <button
                          type="button"
                          onClick={() => push("")}
                          className="mt-2 px-2 py-1 bg-blue-500 text-white text-xs rounded flex items-center gap-2"
                        >
                          <CgAdd /> Add Corporate Code
                        </button>
                      </>
                    )}
                  </FieldArray>
                </div>
              </div>

              <div className="flex justify-end gap-2 p-4 border-t">
                <Button variant="secondary" onClick={closeModal}>
                  Cancel
                </Button>
                <Button type="submit" variant="primary" isLoading={loading}>
                  Create Super Vendor
                </Button>
              </div>
            </form>
          </>
        )}
      </Formik>
    </div>
  );
};

export default CreateVendor;
